import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Modal } from '../admin/Modal';
import { CheckCircle, Trash2 } from 'lucide-react';

/**
 * ActionItem interface - matches backend data model
 */
export interface ActionItem {
  id: string;
  title: string;
  notes: string | null;
  priority: 'urgent' | 'high' | 'medium' | 'low';
  owner_type: 'coach' | 'client';
  due_date: string | null;
  status: 'not_started' | 'in_progress' | 'completed';
  created_by: string;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

interface ActionItemModalProps {
  clientId: string;
  clientName: string;
  item: ActionItem | null; // null = create mode
  onClose: () => void;
  onSave: () => void;
}

/**
 * ACTION ITEM MODAL
 *
 * Create/Edit modal for action items.
 * Uses standard Modal component for consistency.
 *
 * Features:
 * - Create new items
 * - Edit existing items
 * - Mark Complete with confirmation (deliberate action)
 * - Delete with confirmation
 */
export const ActionItemModal: React.FC<ActionItemModalProps> = ({
  clientId,
  clientName,
  item,
  onClose,
  onSave
}) => {
  const isEditMode = item !== null;
  const isCompleted = item?.status === 'completed';

  // Form state
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [priority, setPriority] = useState<'urgent' | 'high' | 'medium' | 'low'>('medium');
  const [ownerType, setOwnerType] = useState<'coach' | 'client'>('client');
  const [dueDate, setDueDate] = useState('');
  const [status, setStatus] = useState<'not_started' | 'in_progress' | 'completed'>('not_started');

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Initialize form with existing item data
  useEffect(() => {
    if (item) {
      setTitle(item.title);
      setNotes(item.notes || '');
      setPriority(item.priority);
      setOwnerType(item.owner_type);
      setDueDate(item.due_date || '');
      setStatus(item.status);
    } else {
      // Reset to defaults for create mode
      setTitle('');
      setNotes('');
      setPriority('medium');
      setOwnerType('client');
      setDueDate('');
      setStatus('not_started');
    }
    setHasChanges(false);
  }, [item]);

  // Track changes
  const handleFieldChange = <T,>(setter: React.Dispatch<React.SetStateAction<T>>, value: T) => {
    setter(value);
    setHasChanges(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const url = isEditMode
        ? `/api/clients/${clientId}/action-items/${item.id}`
        : `/api/clients/${clientId}/action-items`;

      const payload = {
        title: title.trim(),
        notes: notes.trim() || null,
        priority,
        owner_type: ownerType,
        due_date: dueDate || null,
        ...(isEditMode && { status })
      };

      const response = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to save action item');
      }

      // STOPGAP: Per-component email toast — pending systemic API wrapper refactor
      // See Kanban card for full cleanup plan (includes AllNotesWorkspace, ActionItemModal, goals)
      if (!isEditMode) {
        const data = await response.json();
        if (data.emailSent === true) {
          toast.success('Action item created and emailed to client');
        } else if (data.emailSent === false) {
          toast.error(`Action item created. Email delivery failed: ${data.emailError || 'Unknown error'}`);
        }
      }

      onSave();
    } catch (err) {
      console.error('Error saving action item:', err);
      setError(err instanceof Error ? err.message : 'Failed to save action item');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!item) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `/api/clients/${clientId}/action-items/${item.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete action item');
      }

      onSave();
    } catch (err) {
      console.error('Error deleting action item:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete action item');
      setShowDeleteConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  // Confirmation dialog for Delete
  if (showDeleteConfirm) {
    return (
      <Modal
        isOpen={true}
        onClose={() => setShowDeleteConfirm(false)}
        title="Delete Action Item"
        size="xs"
        variant="danger"
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleDelete}
              disabled={loading}
            >
              {loading ? 'Deleting...' : 'Yes, Delete'}
            </button>
          </>
        }
      >
        <div className="confirm-content">
          <p>Are you sure you want to delete this action item? This cannot be undone.</p>
          <div className="confirm-task-preview">
            <strong>{item?.title}</strong>
          </div>
        </div>
        <style>{`
          .confirm-content p {
            color: var(--text-secondary);
            margin: 0 0 16px 0;
            line-height: 1.5;
          }
          .confirm-task-preview {
            background: var(--bg-secondary);
            padding: 12px;
            border-radius: var(--radius-medium);
          }
          .confirm-task-preview strong {
            display: block;
            color: var(--text-primary);
          }
        `}</style>
      </Modal>
    );
  }

  // Use wider modal for edit mode (more content to display)
  const modalSize = isEditMode ? 'medium' : 'small';

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={isEditMode ? 'Action Item Details' : 'New Action Item'}
      size={modalSize}
      variant="info"
      hasUnsavedChanges={hasChanges}
      footer={
        <>
          {isEditMode && (
            <button
              type="button"
              className="btn btn-danger-filled"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={loading}
              style={{ marginRight: 'auto' }}
            >
              <Trash2 size={16} />
              Delete
            </button>
          )}
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            {isEditMode ? 'Close' : 'Cancel'}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loading || !title.trim()}
          >
            {loading ? 'Saving...' : isEditMode ? 'Save Changes' : 'Create Task'}
          </button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="action-item-form">
        {error && (
          <div className="form-error">
            {error}
          </div>
        )}

        {/* Completed status banner */}
        {isCompleted && (
          <div className="completed-banner">
            <CheckCircle size={16} />
            <span>Completed {item?.completed_at ? new Date(item.completed_at).toLocaleDateString() : ''}</span>
          </div>
        )}

        {/* Client context */}
        <div className="form-context">
          Task for: <strong>{clientName}</strong>
        </div>

        {/* Title + Assigned To */}
        <div className="form-row form-row-title">
          <div className="form-group form-group-title">
            <label htmlFor="title">Title *</label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => handleFieldChange(setTitle, e.target.value)}
              placeholder="Enter task title..."
              autoFocus={!isEditMode}
              disabled={loading}
            />
          </div>
          <div className="form-group form-group-assigned">
            <label htmlFor="ownerType">Assigned To</label>
            <select
              id="ownerType"
              value={ownerType}
              onChange={(e) => handleFieldChange(setOwnerType, e.target.value as 'coach' | 'client')}
              disabled={loading}
            >
              <option value="client">Client</option>
              <option value="coach">Coach</option>
            </select>
          </div>
        </div>

        {/* Notes */}
        <div className="form-group">
          <label htmlFor="notes">Notes</label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e) => handleFieldChange(setNotes, e.target.value)}
            placeholder="Add details or instructions..."
            rows={5}
            disabled={loading}
          />
        </div>

        {/* Due Date + Priority + Status (edit mode) */}
        <div className="form-row form-row-controls">
          <div className="form-group">
            <label htmlFor="dueDate">Due Date</label>
            <input
              id="dueDate"
              type="date"
              value={dueDate}
              onChange={(e) => handleFieldChange(setDueDate, e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="priority">Priority</label>
            <select
              id="priority"
              value={priority}
              onChange={(e) => handleFieldChange(setPriority, e.target.value as 'urgent' | 'high' | 'medium' | 'low')}
              disabled={loading}
              className={priority === 'urgent' ? 'priority-urgent' : ''}
            >
              <option value="urgent">URGENT</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          {isEditMode && (
            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                value={status}
                onChange={(e) => handleFieldChange(setStatus, e.target.value as 'not_started' | 'in_progress' | 'completed')}
                disabled={loading}
                className={status === 'completed' ? 'status-completed' : ''}
              >
                <option value="not_started">Not Started</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          )}
        </div>
      </form>

      <style>{`
        .action-item-form {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .form-error {
          background: rgba(220, 38, 38, 0.1);
          border: 1px solid var(--accent-red);
          color: var(--accent-red);
          padding: 12px;
          border-radius: var(--radius-medium);
          font-size: 14px;
        }

        .completed-banner {
          display: flex;
          align-items: center;
          gap: 8px;
          background: rgba(52, 199, 89, 0.1);
          border: 1px solid var(--accent-green);
          color: var(--accent-green);
          padding: 12px;
          border-radius: var(--radius-medium);
          font-size: 14px;
          font-weight: 500;
        }

        .form-context {
          font-size: 14px;
          color: var(--text-secondary);
          padding-bottom: 8px;
          border-bottom: 1px solid var(--border-primary);
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
          flex: 1;
        }

        .form-group label {
          font-size: 13px;
          font-weight: 500;
          color: var(--text-secondary);
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          padding: 10px 12px;
          font-size: 14px;
          border: 1px solid var(--border-primary);
          border-radius: var(--radius-medium);
          background: var(--bg-secondary);
          color: var(--text-primary);
          transition: border-color 0.2s ease;
        }

        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: var(--accent-blue);
        }

        .form-group textarea {
          resize: vertical;
          min-height: 80px;
        }

        .form-group input:disabled,
        .form-group select:disabled,
        .form-group textarea:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .form-row {
          display: flex;
          gap: 16px;
        }

        .form-row .form-group {
          flex: 1;
        }

        .form-row-title {
          display: flex;
          gap: 16px;
        }

        .form-group-title {
          flex: 1;
        }

        .form-group-assigned {
          width: 95px;
          flex-shrink: 0;
        }

        .form-row-controls {
          display: flex;
          gap: 16px;
        }

        .form-row-controls .form-group {
          flex: 1;
        }

        .status-completed {
          background: rgba(52, 199, 89, 0.15) !important;
          border-color: var(--accent-green) !important;
          color: var(--accent-green) !important;
        }

        .priority-urgent {
          background: rgba(249, 115, 22, 0.15) !important;
          border-color: var(--accent-orange) !important;
          color: var(--accent-orange) !important;
          font-weight: 600;
        }
      `}</style>
    </Modal>
  );
};

export default ActionItemModal;
